/**
 * The two queries the showings setup (and the guide's state derivation) read: a listing's availability
 * rules and exceptions, and its upcoming open houses. One key, one fetcher each.
 */

import {
  fetchAvailability,
  fetchShowingsForListing,
  type ShowingAppointment,
} from '../../../../lib/leasing.api';

/** A listing's availability rules + exceptions. */
export function availabilityQueryOptions(listingId: string) {
  return {
    queryKey: ['listing', listingId, 'availability'] as const,
    queryFn: () => fetchAvailability(listingId),
  };
}

/** An upcoming scheduled open house (a showing appointment of kind `open_house`). */
export type OpenHouse = ShowingAppointment;

export interface OpenHousesResult {
  /** Soonest first. */
  items: OpenHouse[];
  /** The listing's IANA timezone: open-house dates and times are entered and shown in it. */
  timezone: string;
}

/**
 * Upcoming, still-scheduled open houses. The key sits under ['listing', id, 'showings'] so the
 * Showings tab's appointment list and this one refresh together.
 */
export function openHousesQueryOptions(listingId: string) {
  return {
    queryKey: ['listing', listingId, 'showings', 'open-houses'] as const,
    queryFn: async (): Promise<OpenHousesResult> => {
      const { items, timezone } = await fetchShowingsForListing(listingId, {
        dateFrom: new Date().toISOString(),
      });
      return {
        items: items
          .filter((item) => item.kind === 'open_house' && item.outcome === 'scheduled')
          .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor)),
        timezone,
      };
    },
  };
}
